import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { Hero } from "../components/Hero";
import { Reveal, ClipImage } from "../components/Motion";
import { CustomerGrid } from "../components/Customers";
import { TextMarquee } from "../components/Marquee";
import { CtaBand } from "../components/CtaBand";
import { SERVICES } from "../data/content";

const WhoWeAre = () => (
  <section className="container-x section" data-testid="who-we-are-section">
    <div className="grid gap-20 lg:gap-16 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal>
          <p className="eyebrow mb-6 text-[var(--ink-soft)]">01 — Company</p>
          <h2 className="font-serif-display text-[2rem] sm:text-4xl lg:text-[2.75rem] leading-[1.1]">Who We Are</h2>
        </Reveal>
        <div className="mt-10 max-w-[34rem] space-y-6">
          <Reveal delay={0.05}>
            <p className="lead">1818 Brands is a U.S.-based company specializing in sourcing and distributing branded merchandise for the off-price retail market.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="body-copy">Our business is supported by an international sourcing network built through long-standing relationships across multiple levels of the supply chain.</p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="body-copy">We connect brands, factories and inventory opportunities with major retailers, combining competitive pricing, reliable execution and the ability to handle large-volume transactions.</p>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <Link to="/about" className="group mt-10 inline-flex items-center gap-3 text-sm font-medium" data-testid="home-about-link">
            <span className="link-underline pb-1">More about 1818 Brands</span>
            <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} />
          </Link>
        </Reveal>
      </div>
      <div className="lg:col-span-7 lg:col-start-6 lg:pl-8" data-testid="our-customers-section">
        <Reveal>
          <p className="eyebrow mb-6 text-[var(--ink-soft)]">02 — Retail relationships</p>
          <h2 className="font-serif-display text-[2rem] sm:text-4xl lg:text-[2.75rem] leading-[1.1] mb-10">Our Customers</h2>
        </Reveal>
        <CustomerGrid />
        <p className="mt-5 text-xs leading-relaxed text-[var(--ink-soft)]">
          Retailers we have supplied. Logos are trademarks of their respective owners.
        </p>
      </div>
    </div>
  </section>
);

const ServicesIndex = () => (
  <section className="relative bg-[var(--paper-2)]/60 section" data-testid="home-services-section">
    <div className="container-x">
      <div className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="eyebrow mb-6 text-[var(--ink-soft)]">03 — What we do</p>
            <h2 className="font-serif-display text-[2rem] sm:text-4xl lg:text-[2.75rem] leading-[1.1]">From factory floor to off-price shelf.</h2>
          </Reveal>
          <ClipImage src="/img/warehouse.jpg" alt="Distribution warehouse with stacked cartons" className="mt-12 hidden lg:block aspect-[4/5] w-full" testId="home-services-image" />
        </div>
        <div className="lg:col-span-7 lg:col-start-6 lg:ml-8 border-t border-[var(--ink)]">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.04}>
              <Link
                to={`/services#${s.id}`}
                className="group grid grid-cols-[2.5rem_1fr_auto] sm:grid-cols-[3.5rem_1fr_auto] items-baseline gap-4 border-b border-[var(--line)] py-7 sm:py-8"
                data-testid={`home-service-${s.id}`}
              >
                <span className="text-xs tabular-nums tracking-[0.12em] text-[var(--ink-soft)]">0{i + 1}</span>
                <div>
                  <h3 className="font-serif-display !font-normal text-xl sm:text-[1.5rem] leading-snug transition-transform duration-500 group-hover:translate-x-1.5">{s.title}</h3>
                  <p className="mt-3 max-w-[32rem] body-copy">{s.text}</p>
                </div>
                <ArrowRight className="h-5 w-5 self-center -translate-x-2 opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-x-0 group-hover:opacity-100" strokeWidth={1.25} />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="1818 Brands — Branded Merchandise for Off-Price Retail"
        description="1818 Brands is a U.S.-based company sourcing and distributing branded apparel, footwear and accessories, connecting brands and factories with off-price retailers."
      />
      <Hero />
      <WhoWeAre />
      <ServicesIndex />
      <div className="pb-8 lg:pb-12">
        <TextMarquee words={["Brands", "Factories", "Closeouts", "Excess production", "Off-price retail", "Distribution"]} />
      </div>
      <CtaBand />
    </>
  );
}
